import { Box, Checkbox, Collapse, FormControlLabel, FormGroup, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import { TabletButton } from '@campfire/tablet-button';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { object as YupObject, boolean as YupBoolean } from 'yup';
import { CreateTrialAccountFormValues } from '../CreateTrialAccountTypes';
import { TRIAL_STEPS, TrialStepsEnum } from '../CreateTrialAccountConsts';
import { StepHeader } from './Stepheader';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const TermsOfUseStepSchema = YupObject().shape({
  agreedTerms: YupBoolean()
    .required('The terms and conditions must be accepted')
    .oneOf([true], 'The terms and conditions must be accepted'),
});

const ExpandableSection = ({
  title,
  children,
  isExpanded,
  onToggle,
}: {
  title: string;
  children: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const { theme, isMobile } = useCampfireTheme();

  return (
    <Box mb={2}>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        bgcolor={theme.color.grey.neutralBrand200}
        borderRadius='8px'
        p={2}
        style={{ cursor: 'pointer' }}
        onClick={onToggle}
      >
        <Typography variant='body1' style={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {isExpanded ? (
          <KeyboardArrowUp style={{ color: theme.palette.grey[600] }} />
        ) : (
          <KeyboardArrowDown style={{ color: theme.palette.grey[600] }} />
        )}
      </Box>
      <Collapse in={isExpanded}>
        <Box
          bgcolor='#f1f1f1'
          borderRadius='0 0 15px 15px'
          border={`1px solid ${theme.color.grey.neutralBrand200}`}
          borderTop='none'
          p={isMobile ? 2 : 3}
        >
          <Box
            bgcolor='#ffffff'
            p={isMobile ? 2 : 5}
            height='180px'
            overflow='auto'
            sx={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              },
            }}
          >
            {children}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const TermsAndConditionsContent = () => (
  <Typography style={{ fontSize: '11px', fontWeight: 400 }}>
    <h2>1. Introduction</h2>

    <p>
      1.1. Volaby Australia Pty Ltd owns, operates and provides Volaby. Volaby Australia Pty Ltd also owns all domain
      names and web pages arising out of Volaby.
    </p>

    <p>
      1.2. These T&Cs are important and govern your use of Volaby and the provision of any Services to you. Please read
      them carefully. By accessing and using Volaby and procuring the Services you warrant that you have read, accept
      and agree to the T&Cs. Volaby Australia Pty Ltd reserves the right to change the T&Cs at any time by publishing
      updates on its website, and may provide you with a notice of the update to you registered email address. By
      continuing to use Volaby and procuring the Services you agree to be bound by the changes to the T&Cs. If you do
      not agree with the T&Cs you must not use Volaby.
    </p>

    <p>
      1.3. In these T&Cs, you or your means person or entity registered who is a User, and where relevant any of your
      Personnel and Authorised Representatives.
    </p>

    <p>
      1.4. You and any Authorised Representative must be at least 18 years of age to use Volaby. You and any Authorised
      Representative must not access or use Volaby if you are under the age of 18.
    </p>

    <p>
      1.5. All Authorised Representative who use Volaby warrant that they are an authorised representative of a User,
      and have the requisite authority to act for and on behalf of the User in relation to all matters in connection
      with Volaby and the T&Cs.
    </p>

    <p>
      1.6. If you agree to the T&Cs, Volaby Australia Pty Ltd grants you a revocable, non-exclusive, non-assignable and
      non-transferable licence to use Volaby under the T&Cs for the Term.
    </p>

    <p>
      1.7. Except as provided in the T&Cs, these T&Cs do not relate to your use of any product, service, process or
      technology described on Volaby and you are referred to the individual product warranty relevant to that particular
      product, service, process or technology.
    </p>

    <h2>2. Definitions</h2>

    <p>
      2.1. In these T&Cs, the following words and expressions have these meanings unless the context otherwise requires:
    </p>

    <ul>
      <li>
        (a) <strong>Account</strong> means the account that provides access to Volaby and facilitates your use of
        Volaby.
      </li>

      <li>
        (b) <strong>Authorised Representative</strong> means any person who has authority to act for and on behalf of a
        User who is authorised to use and access Volaby on the User.
      </li>

      <li>
        (c) <strong>Claims</strong> means all demands, claims, proceedings, penalties, fines and liability (whether
        criminal or civil, in contract, tort or otherwise).
      </li>

      <li>
        (d) <strong>Confidential Information</strong> means information (in any form) that relates to the business
        assets or affairs of the discloser and which is either:
        <ul>
          <li>
            (i) made available by or on behalf of the discloser to the recipient during or in connection with these
            T&Cs;
          </li>
          <li>
            (ii) information concerning the existence and terms of these T&Cs (including the Fees and the scope of
            Services);
          </li>
          <li>(iii) expressly stated to be or marked confidential;</li>
          <li>(iv) by its nature confidential; or</li>
          <li>(v) known to be, or ought to be known to, confidential by the recipient.</li>
        </ul>
        Confidential Information does not include information which is:
        <ul>
          <li>(vi) in the public domain, other than due to a breach of confidentiality; or</li>
          <li>
            (vii) lawfully obtained by the recipient from a different source in circumstances which do not impose a duty
            of confidence.
          </li>
        </ul>
      </li>

      <li>
        (e) <strong>Content</strong> means any words, communications, files, data, photos, pictures, documents, audio,
        films, telecasts, digital works, livestreams, videos, media, designs and devices that you upload, input, store,
        broadcast or share through Volaby.
      </li>

      <li>
        (f) <strong>Credentials</strong> means your username, password and other credentials in connection with your
        Account.
      </li>

      <li>
        (g) <strong>Customer Data</strong> means all data provided by or on behalf of you or your Authorised
        Representatives to Volaby Australia Pty Ltd (including through use of the Services) under these T&Cs and
        includes User Profile Information.
      </li>

      <li>
        (h) <strong>Customer Material</strong> means Customer Data and other Materials provided by or on behalf of you
        or your Authorised Users to Volaby Australia Pty Ltd (including through use of the Services) under these T&Cs.
      </li>

      <li>
        (i) <strong>Deliverable</strong> means any reports, documentation or other Materials to be generated, processed
        or supplied by or on behalf of Volaby Australia Pty Ltd in connection with the Services, which is provided to
        you or your Authorised Representatives in connection with the Services, including through the use of Volaby.
      </li>

      <li>
        (j) <strong>Defects</strong> means a defect, error, omission, failure, shrinkage, subsidence, irregularity or
        other aspect in or of the Services or a Deliverable, in each case, which does not comply in a material respect
        with these T&Cs, except to the extent caused by fair wear and tear.
      </li>

      <li>
        (k) <strong>Developed Material</strong> means any Material developed by or provided by or on behalf of Volaby
        Australia Pty Ltd in connection with these T&Cs, which is provided to you or your Authorised Representatives in
        the course of providing the Services, including any Deliverables
      </li>

      <li>
        (l) <strong>Dispute</strong> means any dispute or difference between the parties arising out of, relating to or
        in connection with these T&Cs and the Services, including any dispute or difference as to the formation,
        validity, existence or termination of these T&Cs.
      </li>

      <li>
        (m) <strong>Documentation</strong> means all documentation in any medium which Volaby Australia Pty Ltd is
        required to prepare, produce or recommend (including any produced by Volaby Australia Pty Ltd&apos;s Personnel)
        as specified in any Service Order Form and the operating manuals, user guides and any other documentation which
        Volaby Australia Pty Ltd generally makes available to its customers in connection with the Services
      </li>

      <li>
        (n) <strong>Fee</strong> means the fees payable by you for the Services and access to Volaby as specified in the
        Service Order Form and any other fees may be as published on Volaby Australia Pty Ltd&apos;s website from time
        to time.
      </li>

      <li>
        (o) <strong>Government Agency</strong> means any government or governmental, administrative, monetary, fiscal or
        judicial body, department, commission, authority, tribunal, agency or entity in Australia or New Zealand.
      </li>

      <li>
        (p) <strong>GST</strong> means a goods and services tax or similar value added tax levied or imposed in
        Australia under the GST Law.
      </li>

      <li>
        (q) <strong>GST Law</strong> means the same as &quot;GST law&quot; means in the A New Tax System (Goods and
        Services Tax) Act 1999 (Cth).
      </li>

      <li>
        (r) <strong>Intellectual Property Rights</strong> means the rights of a creator or an owner relating to
        copyright, trade marks, designs, patents, circuit layouts, plant varieties, inventions and Confidential
        Information, and other results of intellectual activity whether or not in material form, and any application or
        right to apply for registration of any of these rights, but excluding Moral Rights.
      </li>

      <li>
        (s) <strong>Links</strong> means links or other connections to websites operated by parties other than Volaby
        Australia Pty Ltd.
      </li>

      <li>
        (t) <strong>Losses</strong> means all losses including financial losses, damages, legal costs and other expenses
        of any nature whatsoever.
      </li>

      <li>
        (u) <strong>Material</strong> includes domain names, names, text, graphics, images, photographs, illustrations,
        diagrams, logos, buttons, icons, software, and all products, services, processes and technologies described on
        Volaby, any Developed Material and Documentation.
      </li>

      <li>
        (v) <strong>Moral Rights</strong> means rights of integrity of authorship, rights of attribution or authorship,
        rights not to have authorship falsely attributed, and rights of a similar nature conferred by statute in
        Australia that may now exist or that may come to exist in relation to the work.
      </li>

      <li>
        (w) <strong>Volaby Australia Pty Ltd</strong> means Volaby Australia Pty Ltd Limited (ABN 21 676 942 840).
      </li>

      <li>
        (x) <strong>Payment Processor</strong> means a third party entity appointed by Volaby Australia Pty Ltd from
        time to time that manages and operates any payments by you to Volaby Australia Pty Ltd. Currently appointed
        payment processor is &quot;Stripe&quot; and their terms and conditions can be found at https://stripe.com/ssa/.
      </li>

      <li>
        (y) <strong>Personal Information</strong> has the meaning given by the Privacy Act.
      </li>

      <li>
        (z) <strong>Personnel</strong> means in relation to a Party, any employees, secondees, agents, principals,
        contractors (excluding the other Party) and subcontractor of that party
      </li>

      <li>
        (aa) <strong>Volaby</strong> means the online Volaby, website and/or mobile phone application provided by Volaby
        Australia Pty Ltd the provision of the Services;
      </li>

      <li>
        (bb) <strong>Privacy Act</strong> means the Privacy Act 1988 (Cth).
      </li>

      <li>
        (cc) <strong>Senior Management Representative</strong> means in respect of a party, the person specified in the
        Service Order Form, or if no such person is specified, that party&apos;s chief executive officer or person
        holding an equivalent position
      </li>

      <li>
        (dd) <strong>Sensitive Information</strong> has the meaning given by the Privacy Act.
      </li>

      <li>
        (ee) <strong>Services</strong> means the relevant services provided Volaby Australia Pty Ltd as specified in the
        Service Order Form, including any Deliverables and Support Services.
      </li>

      <li>
        (ff) <strong>Service Interruptions</strong> means any time during which a User cannot access a Volaby, but does
        not include any time during which maintenance is being performed on the Volaby and its underlying systems.
      </li>

      <li>
        (gg) <strong>Service Levels</strong> means the service level targets, if any, specified in a Service Order Form.
      </li>

      <li>
        (hh) <strong>Service Order Form</strong> means an order form prepared by Volaby Australia Pty Ltd, including
        electronically, or as auto-generated on Volaby for the provision of the Services.
      </li>

      <li>
        (ii) <strong>T&Cs</strong> means these Terms and Conditions of Use, as varied from time to time.
      </li>

      <li>
        (jj) <strong>Term</strong> means the period the Services will be provided as specified in the Service Order
        Form.
      </li>

      <li>
        (kk) <strong>User</strong> means the person or entity (and their Authorised Representative) who have registered
        an Account and access and use Volaby.
      </li>

      <li>
        (ll) <strong>User Profile Information</strong> means information about a User or a prospective User, provided to
        Volaby Australia Pty Ltd by that individual or by or on behalf of you, to facilitate the enrolment of that
        individual a User and their ongoing access to Volaby.
      </li>
    </ul>

    <h2>3. Interpretation</h2>

    <p>3.1. In these T&Cs, unless the context otherwise requires:</p>
    <ul>
      <li>(a) words importing any gender include every gender;</li>
      <li>(b) words importing the singular number include the plural number and vice versa;</li>
      <li>(c) words importing persons include firms, companies and corporations and vice versa;</li>
      <li>
        (d) references to numbered clauses, paragraphs and schedules are references to the relevant clause or paragraph
        in or schedule to these T&Cs;
      </li>
      <li>
        (e) reference in any schedule to these T&Cs to numbered paragraphs relate to the numbered paragraphs of that
        schedule;
      </li>
      <li>
        (f) any obligation on any party not to do or omit to do anything is to include an obligation not to allow that
        thing to be done or omitted to be done;
      </li>
      <li>(g) the headings to the clauses and schedules of these T&Cs are not to affect the interpretation;</li>
      <li>
        (h) any reference to an enactment includes reference to that enactment as amended or replaced from time to time
        and to any subordinate legislation or by-law under that enactment; and
      </li>
      <li>
        (i) the word &apos;including&apos; (and related forms including &apos;includes&apos;) means &apos;including
        without limitation&apos;.
      </li>
    </ul>

    <h2>4. Volaby and Services</h2>

    <h3>Services</h3>

    <p>
      4.1. Volaby Australia Pty Ltd has developed Volaby and content to provide a volunteer management system and
      services to you.
    </p>

    <p>
      4.2. You may request Volaby Australia Pty Ltd to provide services, and Volaby Australia Pty Ltd may provide you
      with a Service Order Form.
    </p>

    <p>
      4.3. You must sign and return/or submit on Volaby the Service Order Form to Volaby Australia Pty Ltd which
      constitutes as an offer to procure the Services from Volaby Australia Pty Ltd Subject to these T&Cs.
    </p>

    <p>
      4.4. Volaby Australia Pty Ltd at its absolute discretion may agree to provide you with the Services as specified
      in a Services Order Form by accepting the Service Order Form and providing access to Volaby.
    </p>

    <p>
      4.5. You agree that by signing/submitting a Service Order Form and Volaby Australia Pty Ltd providing you with
      access to Volaby creates a legally binding agreement for the provision of the Services by Volaby Australia Pty Ltd
      to you in accordance with these T&Cs.
    </p>

    <p>
      4.6. Where an inconsistency exists between these T&Cs and a matter contained in the Service Order Form with You,
      the following order of precedence will apply:
    </p>
    <ul>
      <li>(a) the T&Cs;</li>
      <li>(b) the Service Order Form</li>
    </ul>

    <h3>Term</h3>

    <p>4.7. Volaby Australia Pty Ltd will provide the Services and access to Volaby for the Term.</p>

    <p>
      4.8. Upon expiry of the Terms, if requested by you at least 60 days prior to the expiry of the Term, Volaby
      Australia Pty Ltd may, at its absolute discretion, continue to provide you with the Services and access to Volaby
      on a month to month basis and otherwise on the same terms and conditions, in which case either party can terminate
      on one (1) month&apos;s written notice to the other.
    </p>

    <h3>Documentation</h3>

    <p>
      4.9. Volaby Australia Pty Ltd will provide Documentation as set out in the Service Order Form and to the extent
      otherwise required under these T&Cs, in the form which Volaby Australia Pty Ltd generally makes available to its
      customers.
    </p>

    <h3>Services Levels</h3>

    <p>4.10. Volaby Australia Pty Ltd will</p>
    <ul>
      <li>(a) use reasonable endeavours to provide the Services in accordance with the Service Levels;</li>
      <li>(b) measure, monitor and record failures to meet the applicable Service Levels; and</li>
      <li>
        (c) unless otherwise specified in a Service Order Form, use reasonable endeavours to identify and address the
        root cause of any issues giving rise to a failure to meet a Service Level
      </li>
    </ul>

    <h3>Volaby availability</h3>

    <p>
      4.11. Volaby Australia Pty Ltd tries to make Volaby available at all times but does not guarantee that access to
      Volaby will be continuous or fault-free. However, Volaby may be unavailable from time to time, including
      occasional disruptions and outages are inevitable.
    </p>

    <p>
      4.12. Volaby may also become unavailable if Volaby Australia Pty Ltd needs to maintain or upgrade Volaby. Volaby
      will use reasonable endeavours to notify you in advance of the timing for the updates.
    </p>

    <p>
      4.13. Volaby Australia Pty Ltd is not liable for any disruption, Claims or Losses that you may suffer in
      connection with any unavailability of Volaby.
    </p>

    <h3>Support Services</h3>

    <p>
      4.14. Volaby Australia Pty Ltd will maintain a support desk to be provided by its third party provider, Intercom
      Support, that is available to you during any support hours specified in the Service Order Form to enable you to
      report Service Interruptions and Defects to Volaby Australia and seek answers to technical questions relating to
      Volaby.
    </p>

    <p>
      4.15. Volaby Australia Pty Ltd will use reasonable endeavours to respond to all questions and requests made to the
      support desk and resolve all technical questions relating to Volaby as soon as reasonably practicable.
    </p>

    <p>
      4.16. Volaby Australia Pty Ltd will use reasonable endeavours to monitor Volaby 24 hours a day, 7 days a week for
      Service Interruptions and Defects.
    </p>

    <p>
      4.17. Any Service Interruption or Defects will be reported to you as soon as reasonably practicable and Volaby
      Australia Pty Ltd will use reasonable endeavours to respond to, restore and resolve the Service Interruption or
      Defects as soon as reasonably practicable
    </p>

    <h2>5. Accounts</h2>

    <p>5.1. In order to obtain the Services and use and access Volaby, you must register for an Account.</p>

    <p>5.2. A User may only operate one Account.</p>

    <p>5.3. Volaby Australia Pty Ltd may also change registration requirements from time to time.</p>

    <p>
      5.4. In registering for an Account, you may be required to verify your identity by supplying forms of
      identification and any other information reasonably required. This may include but is not limited to your current
      mobile telephone number, your email address, your surname and your date of birth, account and credit card
      information and verification of identity information.
    </p>

    <p>5.5. You must provide Credentials when you register for an Account.</p>

    <p>
      5.6. You are responsible for correctly registering for an Account and for the accuracy of any information that you
      enter into Volaby.
    </p>

    <p>
      5.7. You must not provide any false, inaccurate, irrelevant, or misleading information when you register for an
      Account.
    </p>

    <p>
      5.8. Should any of your information change, you must immediately update your information by signing into your
      Account and making the necessary updates and or changes.
    </p>

    <p>
      5.9. Volaby Australia Pty Ltd may cancel your Account if Volaby Australia Pty Ltd reasonably considers that you
      have provided false, inaccurate, irrelevant or misleading information.
    </p>

    <p>
      5.10. To protect your Personal Information and Sensitive Information, Volaby Australia Pty Ltd may restrict access
      to your Account until your identity can be verified.
    </p>

    <p>
      5.11. You are entirely and solely responsible for the security of and maintaining the confidentiality of your
      Credentials and for all activities that occur under your Account.
    </p>

    <p>
      5.12. You must notify Volaby Australia Pty Ltd immediately of any unauthorised use of your Account, your
      Credentials, or any other breach of security.
    </p>

    <p>
      5.13. You are responsible and liable for any person, including an Authorised Representative that uses your
      Account.
    </p>

    <p>
      5.14. Volaby Australia Pty Ltd is not liable for any Claims or Losses that you may incur in relation to someone
      else using your Account or Credentials.
    </p>

    <p>
      5.15. Volaby Australia Pty Ltd may take any action it considers necessary to ensure the safety of your Account,
      your Credentials and Volaby. This action may include without limitation cancelling your Account, changing your
      Credentials or requesting additional information to authorise activities relating to your Account or your
      Credentials.
    </p>

    <h2>6. Authorised Representatives</h2>

    <p>
      6.1. The maximum number of Authorised Representatives who may use and access Volaby is as specified in the Service
      Order Form.
    </p>

    <p>
      6.2. You may substitute, decrease or increase the number of Authorised Representatives (subject to the maximum
      number specified in the Service Order Form) at any time by making a request to Volaby Australia Pty Ltd in the
      form and format notified to you by Volaby Australia Pty Ltd from time to time. No additional fees are payable
    </p>

    <p>
      6.3. You may make a request to Volaby Australia Pty Ltd to increases the maximum number of Authorised
      Representatives which Volaby may agree to at its absolute discretion. If Volaby Australia Pty Ltd to increases the
      maximum number of Authorised Representatives, an additional fee pay be payable and the Service Order Form will be
      varied accordingly.
    </p>

    <p>
      6.4. You acknowledge that you are responsible for its and any Authorised Representatives&apos; use of Volaby
      Platform and for all of its communication and activity on Volaby Platform. If Volaby Australia Pty Ltd determines
      that Authorised Representative has engaged in prohibited activities or has otherwise violated these T&Cs, Volaby
      Australia Pty Ltd may deny the Authorised Representative access to Volaby Platform on a temporary or permanent
      basis.
    </p>

    <h2>7. Payment of Fees</h2>

    <p>7.1. You agree to pay the Fees and the manner specified in Service Order Form.</p>

    <h3>Late Fees</h3>

    <p>
      7.2. You must pay interest on any amounts remaining unpaid after the date for payment, calculated (on a daily
      basis) at an annual rate equivalent to the official cash rate set by the Reserve Bank of Australia plus 2%, in
      accordance with the terms of payment set out in the Service Order Form. You agree that the interest payable under
      this clause is a genuine pre-estimate of the loss suffered by Volaby Australia Pty Ltd due to the Purchaser&apos;s
      failure to make payment on time.
    </p>

    <h3>GST</h3>

    <p>
      7.3. All consideration to be paid or provided under the T&Cs is expressed exclusive of GST unless otherwise
      stated. If GST applies to a supply made under the T&Cs and the consideration is expressed exclusive of GST, the
      recipient must pay to the supplier an additional amount equal to the GST payable on the supply (&quot;GST
      Amount&quot;). The GST Amount is payable at the same time that the first part of the consideration for the supply
      is to be provided.
    </p>

    <h3>Payment Processor</h3>

    <p>
      7.4. Volaby Australia Pty Ltd uses a Payment Processor and may require you to add your credit or debit card
      information to your Account. By submitting your payment information, you give Volaby Australia Pty Ltd the right
      to share your payment information with the Payment Processor to process payments. Volaby Australia Pty Ltd is not
      responsible for errors made by the Payment Processor. In so far as it is relevant, the terms and conditions of the
      Payment Processor are incorporated into these T&Cs and will prevail over these T&Cs to the extent of any
      inconsistency.
    </p>

    <h2>8. Privacy Policy</h2>

    <p>
      8.1. The Privacy Policy is incorporated into and forms part of these T&Cs. Volaby Australia Pty Ltd manages and
      deals with your Personal Information in accordance with the Privacy Policy as amended from time to time.
    </p>

    <h2>9. Confidentiality</h2>

    <p>
      9.1. You and Volaby Australia Pty Ltd each cannot use Confidential Information of the other party unless it is
      necessary to exercise its rights or perform its obligations under these T&Cs.
    </p>

    <p>
      9.2. Each party must not disclose Confidential Information of the other party in any circumstance without prior
      consent from the owner of the Confidential Information unless:
    </p>
    <ul>
      <li>
        (a) the Confidential Information is disclosed to a party&apos;s Personnel solely to exercise the party&apos;s
        rights or to comply with the party&apos;s obligations under these T&Cs; or
      </li>
      <li>
        (b) the disclosure is authorised or required by any law, stock exchange or Government Agency or law to be
        disclosed.
      </li>
    </ul>

    <p>9.3. If a party intends on making a disclosure under clause 9.2(b), that party must:</p>
    <ul>
      <li>(a) to the extent possible, notify the other party immediately of the intended disclosure;</li>
      <li>(b) consult with and follow any reasonable directions from the other party to minimise disclosure;</li>
      <li>
        (c) use reasonable efforts to ensure that any Confidential Information disclosed is kept confidential; and
      </li>
      <li>
        (d) if disclosure cannot be avoided, only disclose Confidential Information to the extent necessary to comply.
      </li>
    </ul>

    <p>
      9.4. Volaby Australia Pty Ltd may disclose any Confidential Information to an Authorised Representative or your
      Personnel if it is required to do so to comply with its obligations under these T&Cs.
    </p>

    <p>
      9.5. On termination or expiry of these T&Cs, unless otherwise specified, a party must if requested in writing by
      the other party erase or destroy all documents, materials or intangible records containing, recording or referring
      in any way to the Confidential Information of the requesting party which are in the possession, power or control
      of the party or of any person whom the party has given access.
    </p>

    <h2>10. Publicity</h2>

    <p>
      10.1. You must comply with any reasonable direction that Volaby Australia Pty Ltd provides you relating to any
      public announcements by you about:
    </p>
    <ul>
      <li>(a) the subject matter or provisions of these T&Cs;</li>
      <li>(b) any of the Services; or</li>
      <li>(c) a Deliverable.</li>
    </ul>

    <h2>11. Intellectual property rights</h2>

    <h3>General</h3>

    <p>
      11.1. Volaby Australia Pty Ltd is the owner of all right, title and interest (including present and future
      copyright) in Volaby.
    </p>

    <p>
      11.2. All Material, other than Customer Material, including the Services are owned exclusively by Volaby Australia
      Pty Ltd, its related bodies corporate or others who have licensed their material to Volaby Australia Pty Ltd
      (unless expressly indicated otherwise). Volaby Australia Pty Ltd grants you a non‑exclusive, royalty-free,
      non-transferable personal licence to use the OSA Material during the Term for the Permitted Purposes in accordance
      with the T&Cs.
    </p>

    <p>
      11.3. For the avoidance of any doubt, all product and service names, design marks and slogans are the trade marks
      or service marks of Volaby Australia Pty Ltd.
    </p>

    <h3>Your use of Volaby</h3>

    <p>
      11.4. Nothing in these T&Cs or Volaby will be construed as granting to you ownership of the Material (excluding
      Customer Material) or any proprietary right to the copyright, trade marks, patents or other Intellectual Property
      Rights of Volaby Australia Pty Ltd or any third party.
    </p>

    <p>
      11.5. You may not sell, modify, copy, distribute, transmit, display, perform, reproduce, republish, licence,
      frame, upload, transmit, post, communicate or use the Materials except:
    </p>
    <ul>
      <li>(a) as expressly authorised either in Volaby or these T&Cs; or</li>
      <li>(b) as authorised in writing by Volaby Australia Pty Ltd.</li>
    </ul>

    <p>
      11.6. You may not use any trade mark or service mark appearing on this Volaby without the prior written consent of
      Volaby Australia Pty Ltd or the owner of the trade mark.
    </p>

    <p>
      11.7. You must not use any spider or similar automated software or device to use or access Volaby or the Materials
      in any way.
    </p>

    <p>
      11.8. You grant Volaby Australia Pty Ltd an unrestricted, worldwide, royalty-free licence to use, reproduce,
      modify and adapt any content and information posted on Volaby for the purpose of publishing material on Volaby and
      as otherwise may be required to provide Volaby Australia Pty Ltd, for the general promotion of Volaby Australia
      Pty Ltd and Volaby.
    </p>

    <h3>Customer Material</h3>

    <p>11.9. Volaby Australia Pty Ltd acknowledges that all Customer Material remains your property.</p>

    <p>
      11.10. Nothing in these T&Cs assigns any Intellectual Property Rights in Customer Material to Volaby Australia Pty
      Ltd.
    </p>

    <p>
      11.11. You grant Volaby Australia Pty Ltd a worldwide, non-exclusive, royalty-free, transferable, perpetual
      licence (with the right to assign and sublicense to its related bodies corporate (as defined in the Corporations
      Act 2001 (Cth)), third party contractors and other service providers) to:
    </p>
    <ul>
      <li>
        (a) retain, use, modify, adapt and reproduce the Customer Material as required and for such time as is necessary
        for the performance of its obligations under these T&Cs and to the limited extent required to enable Volaby
        Australia Pty Ltd to comply with its record keeping obligations under applicable law;
      </li>
      <li>
        (b) retain and use any aggregated or de-identified form of the Customer Data for its internal business purposes
        during and after the Term; and
      </li>
      <li>
        (c) retain, use, modify, adapt and reproduce any User Profile Information for the purpose of allowing Authorised
        Users and prospective Authorised Representatives to use Volaby Australia Pty Ltd&apos;s services in connection
        with other Volaby Australia Pty Ltd customers.
      </li>
    </ul>

    <h3>Developed Material</h3>

    <p>
      11.12. Volaby Australia Pty Ltd will own and retain all Developed Material immediately on and from its creation.
      The Developed Material will be treated as Material but will not apply to the extent that:
    </p>
    <ul>
      <li>(a) you and Volaby Australia Pty Ltd agree in writing that it will be owned and retained by you; or</li>
      <li>(b) it is a modification or enhancement to Customer Material,</li>
    </ul>
    <p>
      in which case you and Volaby Australia Pty Ltd agree that the Developed Material will vest in you immediately on
      and from its creation. The Developed Material will be treated as Customer Material.
    </p>

    <p>
      11.13. Where Volaby Australia Pty Ltd owns any Developed Material, you unconditionally assign, and must procure
      that all you unconditionally assign, to Volaby Australia Pty Ltd:
    </p>
    <ul>
      <li>
        (a) all rights and all Intellectual Property Rights (other than copyright) in and to all Developed Material
        without the need for further assurance; and
      </li>
      <li>
        (b) all copyright in the Developed Material, as an assignment of future property under section 197 of the
        Copyright Act 1968 (Cth) and in equity.
      </li>
    </ul>

    <p>11.14. Where any Developed Material vests in you, Volaby Australia Pty Ltd unconditionally assigns to you:</p>
    <ul>
      <li>
        (a) all rights and all Intellectual Property Rights (other than copyright) in and to that Developed Material
        without the need for further assurance; and
      </li>
      <li>
        (b) all copyright in that Developed Material, as an assignment of future property under section 197 of the
        Copyright Act 1968 (Cth) and in equity.
      </li>
    </ul>

    <h3>Moral Rights</h3>

    <p>
      11.15. You must not do anything that is, or is likely to be, an infringement of, or otherwise inconsistent with,
      any Moral Rights in connection with any Developed Material.
    </p>

    <h3>Further assurances</h3>

    <p>
      11.16. You or Volaby Australia Pty Ltd must, if required by the other party, do all further things and execute all
      further documents necessary to assign all rights and all Intellectual Property Rights as necessary to give effect
      to this clause 11.
    </p>

    <h2>12. Your responsibilities</h2>

    <p>
      12.1. You may only use Volaby for lawful purposes, in a responsible and co-operative manner, and in accordance
      with these T&Cs.
    </p>

    <p>12.2. You must not provide access to Volaby to any person who is not an Authorised Representative.</p>

    <p>
      12.3. It is your responsibility to ensure that your use and the use of any Authorised Representatives of Volaby
      complies with the T&Cs and to seek prior written permission from Volaby Australia Pty Ltd for any uses not
      expressly permitted.
    </p>

    <p>12.4. Volaby Australia Pty Ltd prohibits the use of Volaby, for any of the following:</p>
    <ul>
      <li>
        (a) transmitting or posting any Content that is unlawful, harmful, threatening, abusive, harassing, sexually
        explicit, defamatory, vulgar, obscene, libellous, hateful, racially, ethnically or otherwise objectionable or
        invasive of another&apos;s right of privacy;
      </li>
      <li>(b) transmitting or posting any Content that is false, inaccurate or misleading or deceptive;</li>
      <li>
        (c) impersonating any person or entity or falsely stating or otherwise misrepresenting your affiliation with a
        person or entity;
      </li>
      <li>
        (d) posting or transmitting any Content that infringe any third party&apos;s copyright, patent, trademark, trade
        secret or other proprietary rights or Intellectual Property Rights, rights of publicity, confidentiality or
        privacy or that you do not have a right to reproduce, display or transmit or that infringes the rights of any
        person or entity;
      </li>
      <li>(e) posting or transmitting any Content that contains a virus or corrupted data;</li>
      <li>
        (f) using any device, software, or routine that interferes with or attempts to interfere with the proper
        functionality of Volaby;
      </li>
      <li>
        (g) attempting to decipher, decompile, disassemble or reverse engineer any of the software that makes up Volaby
        or the Materials or for the purpose of creating derivative works from Volaby; and
      </li>
      <li>(h) negatively impacting any other person&apos;s ability to access and use Volaby.</li>
    </ul>

    <p>12.5. You agree to at all times:</p>
    <ul>
      <li>(a) comply with these T&Cs and all applicable laws and regulations;</li>
      <li>(b) only post accurate information on Volaby;</li>
    </ul>

    <p>
      12.6. If you contravene the T&Cs or if you fail to pay the Fees, Volaby Australia Pty Ltd may revoke your licence,
      stop providing Volaby to you and immediately close your Account.
    </p>

    <p>
      12.7. You agree that you contravene the T&Cs and are liable to Volaby Australia Pty Ltd if you permit another
      person to access your Account and that person contravenes the T&Cs.
    </p>

    <h2>13. Secure Data, Links and connections to other parties and other sites</h2>

    <p>
      13.1. Volaby Australia Pty Ltd cannot guarantee that any data transmission is secure. Volaby Australia Pty Ltd
      does not warrant and cannot ensure the security of any information the User transmits via Volaby.
    </p>

    <p>
      13.2. Volaby may contain Links. These Links are provided for convenience only. If you use a Link to access a third
      party website, you do so entirely at your own risk. Volaby Australia Pty Ltd&apos;s inclusion of Links or other
      connections to third party websites does not imply that the link is safe or any endorsement of the material on
      them or any association with their owners or operators. Websites accessed via Links may contain advertisements by
      other people. These advertisements are not recommendations or endorsements by Volaby Australia Pty Ltd and Volaby
      Australia Pty Ltd is not responsible for the products and/or services being advertised.
    </p>

    <h2>14. Legal and Financial</h2>

    <p>
      14.1. The Material is factual information only, is not comprehensive and is for general information purposes only.
    </p>

    <p>14.2. The Material is not advice and you may not rely on it as such.</p>

    <p>
      14.3. Volaby Australia Pty Ltd uses reasonable endeavours to ensure the accuracy of the Material and Volaby and is
      not liable for any Loss arising from reliance on Material published on Volaby.
    </p>

    <p>
      14.4. You acknowledge and agree that you have the opportunity to obtain independent legal and financial advice
      that takes into account your personal objectives, financial situation and needs.
    </p>

    <h2>15. Representations and warranties</h2>

    <p>15.1. Volaby Australia Pty Ltd represents and warrants that:</p>
    <ul>
      <li>(a) it will perform its obligations in a good, proper and workmanlike manner;</li>
      <li>
        (b) both it and its Personnel have the skill and expertise to carry out all of Volaby Australia Pty Ltd&apos;s
        obligations; and
      </li>
      <li>
        (c) it has, and will continue to maintain, all rights (including Intellectual Property Rights) and licences
        necessary to perform its obligations and provide the Services.
      </li>
    </ul>

    <h2>16. Disclaimer, warranty exclusions and limitation of liability</h2>

    <p>16.1. You acknowledge and agree that use of Volaby and acceptance of the Services is at your sole risk.</p>

    <p>16.2. Despite clause 15, to the fullest extent permitted by law, Volaby Australia Pty Ltd makes:</p>
    <ul>
      <li>
        (a) any use of the Services or a Deliverable by you which is contrary to these T&Cs or any relevant
        Documentation;
      </li>
      <li>
        (b) any modification or attempted modification of the Services or a Deliverable by you that is not authorised by
        Volaby Australia Pty Ltd or permitted under these T&Cs or any relevant Documentation;
      </li>
      <li>
        (c) any combination of the relevant Services or Deliverable with other software or equipment by you that has not
        been approved by Volaby Australia Pty Ltd; or
      </li>
    </ul>

    <p>
      16.3. any failure or malfunction of any equipment or software not provided by or on behalf of Volaby Australia Pty
      Ltd .
    </p>

    <p>
      16.4. Despite clause 15, to the fullest extent permitted by law, Volaby Australia Pty Ltd makes no representation
      or warranty:
    </p>
    <ul>
      <li>(a) as to the availability of any product, good, service, process or technology described on Volaby;</li>
      <li>(b) that the functions contained in Volaby or your access to Volaby will be error-free;</li>
      <li>(c) that any defects on Volaby or the Materials will be corrected;</li>
      <li>
        (d) that any information, apparatus, product, software or process used or disclosed on Volaby or any Material
        accessible from Volaby will be accurate, up to date, complete or useful; or
      </li>
      <li>
        (e) that Volaby or the server which stores and transmits the Material to you are free from viruses or any other
        harmful components; or
      </li>
      <li>
        (f) in relation to the quality, fitness for use or the security and operation of the Services or that any result
        or objective can or will be achieved or attained at all or by a particular date in the performance of Services,
        whether stated in thse T&Cs, or elsewhere.
      </li>
    </ul>

    <p>
      16.5. To the fullest extent permitted by law, Volaby Australia Pty Ltd its officers, employees, agents and
      contractors (Volaby Australia Pty Ltd&apos;s Group) disclaims all warranties of any kind (whether express or
      implied) in relation to Volaby, the Services and the Material and will not be liable for any direct or indirect
      damage (including special and/or consequential damage, such as loss of business, loss of profits or loss of data)
      which results from any use or access of, or any inability to use or access Volaby, the Services or the Material.
    </p>

    <p>
      16.6. Without limitation to the foregoing, Volaby Australia Pty Ltd&apos;s Group acknowledges that the laws in
      certain jurisdictions including Australia may imply warranties and conditions or impose obligations on the supply
      of goods and services through or by means of Volaby which cannot be excluded, restricted or modified except to a
      limited extent. If any such laws apply, then to the fullest extent permitted by the law of the relevant
      jurisdiction and in the case of Australia to the extent permitted by The Australian Consumer Law in the
      Competition and Consumer Act 2010 (Cth), Volaby Australia Pty Ltd&apos;s Group limits its liability and if any
      liability remains it will be limited to any one or more of the following in its sole discretion:
    </p>
    <ul>
      <li>
        (a) in the case of any goods, the replacement or repair of the goods, or the supply of equivalent goods, or the
        payment of the cost of repairing or replacing the goods or supplying equivalent goods; and
      </li>
      <li>
        (b) in the case of any services, the supply of the services again or the payment of the cost of having the
        services supplied again.
      </li>
    </ul>

    <p>
      16.7. Notwithstanding any other provision to the contrary and to the maximum extent permitted by law, the
      liability of Volaby Australia Pty Ltd for any Claim or Loss is limited to the total amount of Fees paid to Volaby
      Australia Pty Ltd during the 12 months preceding the event giving rise to the relevant claim under these T&Cs
    </p>

    <h2>17. Indemnity</h2>

    <p>
      17.1. You indemnify Volaby Australia Pty Ltd&apos;s Group from and against all Claims, Losses, actions,
      liabilities, costs and expenses (including legal costs on a full indemnity basis) resulting from your failure to
      comply with these T&Cs.
    </p>

    <h2>18. Termination</h2>

    <h3>Breach</h3>

    <p>
      18.1. Volaby Australia Pty Ltd may, in its sole discretion and without prior notice to you, terminate your licence
      to access to Volaby and the Materials if you breach these T&Cs.
    </p>

    <p>
      18.2. You may terminate this agreement between the parties if Volaby Australia Pty Ltd is in breach of its
      obligations under the T&Cs and fails to rectify such breach within a reasonable time (having regard to the nature
      of the breach) of you provide it with written notice of such breach.
    </p>

    <h3>Convenience</h3>

    <p>
      18.3. Either party may terminate this agreement between the parties by providing 7 days&apos; written notice to
      the other.
    </p>

    <h3>Obligations on termination</h3>

    <p>18.4. On termination or expiry:</p>
    <ul>
      <li>
        (a) any unpaid invoices issued to you for Fees are considered immediately due and payable on the termination
        date;
      </li>
      <li>
        (b) if the agreement is terminated pursuant to clause 18.2, you must pay Volaby Australia Pty Ltd that part of
        the Fees which has accrued at the date of termination. Otherwise, you must pay Volaby Australia Pty Ltd all fees
        to the end of the current billing period;
      </li>
      <li>
        (c) Subject to clause 18.5, Volaby Australia Pty Ltd is discharged from any further obligations under these
        T&Cs;
      </li>
      <li>
        (d) Volaby Australia Pty Ltd may:
        <ul>
          <li>(i) retain any moneys paid;</li>
          <li>
            (ii) charge a reasonable sum for work performed in respect of which work no sum has been previously charged;
          </li>
          <li>(iii) pursue any additional or alternative remedies provided by law; and</li>
        </ul>
      </li>
      <li>
        (e) you are not entitled to any refund or repayment of the Fees, for any reason unless termination occurs do to
        a breach by Volaby Australia Pty Ltd.
      </li>
    </ul>

    <p>18.5. For the avoidance of doubt, if this agreement is terminated pursuant to clause 18.3</p>
    <ul>
      <li>
        (a) you must pay all Fees to the end of the current contract term (i.e. if on a monthly plan, then fees are
        payable to the end of the month and if you are on annual plan, then fees are payable to the end of the annual
        period); and
      </li>
      <li>
        (b) access to Volaby will continue until the end of the contract term (i.e. if on a monthly plan, then access
        will be permitted until the end of the month and if you are on annual plan, then access will be permitted until
        the end of the annual period).
      </li>
    </ul>

    <p>
      18.6. You acknowledge and agree that Volaby Australia Pty Ltd will not migrate any data in relation to your
      Account.
    </p>

    <h2>19. Disputes</h2>

    <p>
      19.1. If a Dispute arises, then the following process must be followed before either you or Volaby Australia Pty
      Ltd commences legal proceedings:
    </p>
    <ul>
      <li>
        (a) the party claiming that the Dispute has arisen must refer the Dispute to the Senior Management
        Representatives for resolution by giving notice to the other party;
      </li>
      <li>
        (b) within 5 Business Days of the party giving the notice in subclause (a) above, the Senior Management
        Representatives must meet at least once to attempt to resolve the Dispute;
      </li>
      <li>
        (c) if the Senior Management Representatives do not resolve the dispute within the 5 Business Days in subclause
        (b), either party may refer the Dispute to mediation for resolution by notice to the other party; and
      </li>
      <li>
        (d) within 20 Business Days of the party giving the notice in subclause (c) or such further period as the
        Parties may agree in writing, the Parties must attempt to resolve the Dispute in accordance with the Resolution
        Institute Mediation Rules, as published by the Resolution Institute (ACN 008 651 232 or its successor) and
        current at the time of the mediation.
      </li>
    </ul>

    <p>
      19.2. If the Dispute is not resolved following the process in clause 19.1, then you or Volaby Australia Pty Ltd
      may commence legal proceedings to resolve the Dispute.
    </p>

    <p>
      19.3. Each party warrants that their Senior Management Representative has full authority to resolve any Dispute.
    </p>

    <p>
      19.4. Nothing in this clause will prevent you or Volaby Australia Pty Ltd from instituting proceedings to seek
      urgent injunctive, interlocutory or declaratory relief in respect of a Dispute.
    </p>

    <h2>20. Agreement</h2>

    <p>
      20.1. These T&Cs represent the entire binding agreement between you and Volaby Australia Pty Ltd and our
      respective successors and assigns, and supersede any and all prior understanding, statements or representations,
      whether electronic, oral or written, regarding Volaby Australia Pty Ltd, Volaby or the information and contents on
      Volaby.
    </p>

    <h2>21. Waivers of any obligations or right</h2>

    <p>
      21.1. No waiver of any obligation or right by Volaby Australia Pty Ltd will be effective unless in writing and
      executed by Volaby Australia Pty Ltd.
    </p>

    <h2>22. No Agency</h2>

    <p>
      22.1. No agency, partnership, joint venture, employee-employer or other similar relationship is created by these
      T&Cs. In particular you have no authority to bind Volaby Australia Pty Ltd, its related entities or affiliates in
      any way whatsoever.
    </p>

    <h2>23. Force Majeure</h2>

    <p>
      23.1. Neither party will be liable for any delay or failure to perform its obligations (except for an obligation
      to pay the Fees) if the delay is directly or indirectly due to any occurrence or omission that is beyond the
      reasonable control of that party, including forces of nature, industrial action and action or inaction by a
      Government Agency.
    </p>

    <p>
      23.2. If a delay or failure of a party to perform its obligations is caused or anticipated due to an event
      described by subclause (a), the performance of that party&apos;s obligations will be suspended for the duration of
      the event.
    </p>

    <h2>24. Subcontracting</h2>

    <p>24.1. Volaby Australia Pty Ltd may subcontract the performance of any of its obligations under these T&Cs.</p>

    <h2>25. Assignment</h2>

    <p>
      25.1. You and Volaby Australia Pty Ltd may only assign, encumber, declare a trust over or change control of or
      otherwise deal with its rights under these T&Cs with the written consent of the other party, such consent not to
      be unreasonably withheld or delayed.
    </p>

    <h2>26. Variation</h2>

    <p>
      26.1. Unless otherwise provided for in these T&Cs, the Services or a Service order Form, may not be varied except
      by written agreement duly executed by the parties.
    </p>

    <h2>27. Notices</h2>

    <p>27.1. Any notice, consent or other communication is only effective if it is in writing, signed and either:</p>
    <ul>
      <li>(a) left at the addressee&apos;s address; or</li>
      <li>(b) sent to the addressee&apos;s address by mail, or email,</li>
    </ul>
    <p>as specified in the Service Order Form or as updated by the person notifying the sender.</p>

    <p>
      27.2. A notice, consent or other communication that complies with this clause is regarded as given and received:
    </p>
    <ul>
      <li>(c) if it is delivered, when it has been left at the addressee&apos;s address;</li>
      <li>(d) if it is sent by mail, 3 Business Days after it is posted;</li>
      <li>
        (e) if it is sent in electronic form (email):
        <ul>
          <li>(i) if it is transmitted by 5.00 pm (Brisbane time) on a Business Day – on that Business Day; or</li>
          <li>(ii) otherwise – on the next Business Day.</li>
        </ul>
      </li>
    </ul>

    <h2>28. Costs</h2>

    <p>
      28.1. Each party must pay its own expenses in relation to any negotiation of, execution of, stamping (if relevant)
      and registration costs (if relevant) in connection with these T&Cs.
    </p>

    <h2>29. Severability</h2>

    <p>
      29.1. If any provision in these T&Cs is found to be invalid or unenforceable such invalidity or unenforceability
      will not affect the reminder of these T&Cs and conditions which will continue in full force and effect.
    </p>

    <h2>30. Applicable law</h2>

    <p>
      30.1. Volaby is controlled by Volaby Australia Pty Ltd from its offices in Victoria, Australia. Volaby Australia
      Pty Ltd makes no representation that the Material or Volaby is appropriate or available for use in other
      locations. Those who chose to access Volaby from locations outside of Australia do so at their own initiative and
      risk and are responsible for compliance with local laws.
    </p>

    <p>
      30.2. These T&Cs are governed by and construed in accordance with the laws of Victoria, Australia and you
      irrevocably and unconditionally submit to the exclusive jurisdiction of the courts of Victoria, Australia.
    </p>
  </Typography>
);

const PrivacyPolicyContent = () => (
  <Typography style={{ fontSize: '11px', fontWeight: 400 }}>
    <h2>Privacy Policy</h2>

    <p>
      <strong>Effective: July 1, 2025</strong>
    </p>

    <h2>Introduction</h2>

    <p>
      Volaby is a &apos;Software as a Service&apos; (SaaS) product owned by Volaby Australia Pty Ltd (ABN 21 676 942
      840) and used by &apos;Customer Organisations&apos;.
    </p>

    <p>
      Volaby recognises that the confidentiality of personal information is vitally important and is committed to
      ensuring that information entrusted to Volaby is protected against misuse.
    </p>

    <p>
      The Australian Privacy Principles (APPs), as set out in the Privacy Act 1988 (the &apos;Act&apos;) are applicable
      to Volaby.
    </p>

    <p>
      This policy applies to all employees of Volaby Australia, as well as all organisations and users of Volaby,
      regardless of their status as paying customers or not.
    </p>

    <p>
      Members of customer organisations should read this policy in conjunction with the privacy policy of their
      organisation.
    </p>

    <h2>Policy</h2>

    <p>
      The following policy has been developed to outline the maintenance of privacy within Volaby Australia and customer
      organisations of Volaby.
    </p>

    <p>
      To ensure compliance with its obligations under the Act, Volaby Australia will take all reasonable steps in order
      to comply with the Act. The Volaby Privacy Policy deals with individual rights pursuant to privacy obligations and
      is independent of any contractual and ethical obligations.
    </p>

    <h2>Personal information</h2>

    <p>
      Personal information means information or an opinion about an identified individual, or an individual who is
      reasonably identifiable:
    </p>
    <ul>
      <li>a) Whether the information or opinion is true or not; and</li>
      <li>b) Whether the information or opinion is recorded in a material form or not.</li>
    </ul>

    <p>
      There are some obvious examples of personal information, such as your name or address. Personal information can
      also include medical records, bank account details, photos and videos.
    </p>

    <h2>Privacy process</h2>

    <h3>Collection</h3>

    <p>
      Volaby will only collect information that is necessary to enable customer organisations to perform their services.
      This includes, but is not limited to, information such as:
    </p>
    <ul>
      <li>Name</li>
      <li>Date of birth</li>
      <li>Contact details such as email, address, phone numbers</li>
      <li>Next of kin details</li>
      <li>Relevant medical issues</li>
      <li>Drivers licence details</li>
      <li>Working with children check / working with vulnerable people check details</li>
    </ul>

    <p>
      Volaby will advise when personal information is being collected, for what purpose it has been collected, and how
      such information will be used.
    </p>

    <h3>Use and Disclosure</h3>

    <p>
      Volaby will not use or disclose personal information for a purpose other than the primary purpose of collection
      unless the individual has consented to the use or disclosure.
    </p>

    <h3>Data Quality</h3>

    <p>
      Volaby will take reasonable steps to make sure that the personal information it collects, uses or discloses is
      accurate, complete and up to date. Volaby will take reasonable steps to correct information that is not accurate.
    </p>

    <h3>Storage</h3>

    <p>
      Volaby will take reasonable steps to protect the security of the personal information held in order to avoid the
      misuse, loss, unauthorised access, modification or disclosure of this. Volaby will take reasonable steps to
      destroy personal information if it is no longer needed for any purpose where it may be used or disclosed relating
      to the primary purpose of collection.
    </p>

    <h3>Access</h3>

    <p>
      In appropriate circumstances, Volaby will provide access to the personal information that is held on employees and
      volunteers. If a person requires access to personal information held on them, that information is available to
      them on the Volaby platform.
    </p>

    <p>
      To obtain access to personal information held on you by Volaby, you will be asked to provide proof of your
      identity. This is necessary to ensure that personal information is provided only to the correct person/s and that
      the privacy of others is not undermined.
    </p>

    <p>
      Upon request by an employee or volunteer, Volaby will give the volunteer access to their personal information
      except where an exemption to access under the Act applies.
    </p>

    <p>Employees and volunteers are able to update personal information on the Volaby platform.</p>

    <p>At all times, conduct under this Policy will be governed by the following principles:</p>
    <ul>
      <li>All requests for access will be treated seriously;</li>
      <li>All requests will be dealt with promptly and in a confidential manner.</li>
    </ul>

    <h2>Compliance with the Australian Privacy Principles</h2>

    <p>
      Anyone may make an inquiry or complaint about Volaby&apos;s compliance with the APPs by forwarding an email to
      info@volaby.org or letter to the registered office of Volaby Australia.
    </p>

    <p>
      If an inquiry or complaint is made about Volaby&apos;s compliance with the APPs, Volaby will investigate the
      inquiry or complaint according to the Volaby procedure.
    </p>

    <h2>Employee and Volunteer records</h2>

    <p>
      The Act does not apply to Volaby when it handles current and past employee records for something that is directly
      related to the employment relationship. Volaby will take reasonable steps to ensure that employee records are
      maintained in accordance with the APPs.
    </p>

    <h2>Accountabilities</h2>

    <table cellPadding='5' cellSpacing='0' style={{ border: 1, borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Role</th>
          <th style={{ textAlign: 'left' }}>Accountability</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td valign='top'>
            <strong>Volaby Board Members</strong>
          </td>
          <td>
            Communicate commitment to the Privacy policy
            <br />
            Ensure the policy is amended when required
            <br />
            Model appropriate behaviours
            <br />
            Address potential breaches of this Policy
          </td>
        </tr>
        <tr>
          <td valign='top'>
            <strong>
              Volaby leaders
              <br />
              Customer organisation leaders
            </strong>
          </td>
          <td>
            Ensure that there is consistent interpretation of this policy
            <br />
            Monitor the work environment to ensure that acceptable standards of conduct are observed at all times
            <br />
            Ensure that decisions regarding employees or volunteers and the work environment are in compliance with this
            policy
            <br />
            Model appropriate behaviours
            <br />
            Treat all complaints seriously and take immediate action to refer complaints or report conduct in breach of
            the policy
            <br />
            Act as a first point of contact for employees and volunteers wishing to raise issues that have occurred that
            are contained within this policy
            <br />
            Refer a complaint to another leader if they do not feel that they are the best person to deal with the case
            (e.g. if there is a conflict of interest or if the complaint is complex or serious)
          </td>
        </tr>
        <tr>
          <td valign='top'>
            <strong>Employees and volunteers</strong>
          </td>
          <td>
            Understand their obligations and rights in dealing with personal information
            <br />
            Comply with their responsibilities under this policy
            <br />
            Follow the established procedure should they be involved in collecting personal information for the
            organisation
          </td>
        </tr>
      </tbody>
    </table>

    <h2>Amendments</h2>

    <p>
      This policy may be reviewed annually. Should the review find that there is a need to change the policy, anyone who
      may be impacted by this proposed change, will be advised prior to the change taking place.
    </p>
  </Typography>
);

const TermsOfUseAgreementOption = ({
  trialData,
  next,
  prev,
}: {
  trialData: CreateTrialAccountFormValues;
  next: (newData: CreateTrialAccountFormValues, step: string) => void;
  prev: (newData: CreateTrialAccountFormValues, step: string) => void;
}) => {
  const { theme } = useCampfireTheme();

  const handleSubmit = (values: CreateTrialAccountFormValues) => {
    next(values, TrialStepsEnum.SUBMIT);
  };

  return (
    <Formik
      enableReinitialize={false}
      initialValues={trialData}
      validateOnBlur
      validationSchema={TermsOfUseStepSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <FormGroup style={{ marginTop: '1em' }}>
            <FormControlLabel
              name='agreedTerms'
              value={values.agreedTerms}
              onChange={() => setFieldValue('agreedTerms', !values.agreedTerms)}
              control={<Checkbox color='primary' checked={values.agreedTerms} />}
              label="I agree to Volaby's Terms & Conditions and Privacy Policy"
              style={{
                paddingBottom: '8px',
              }}
            />
            <ErrorMessage
              name='agreedTerms'
              render={(msg) => (
                <Typography variant='caption' style={{ color: '#D93A00' }}>
                  {msg}
                </Typography>
              )}
            />
            <FormControlLabel
              name='newsletter'
              control={<Checkbox color='primary' />}
              label="I'd like to receive The Volaby Voice newsletter with platform updates and insights"
            />
          </FormGroup>
          <Box display='flex' justifyContent='flex-end' pt='28px'>
            <TabletButton
              size='medium'
              style={{
                backgroundColor: theme.color.grey.neutral200,
                color: '#ffffff',
                boxSizing: 'border-box',
                borderRadius: '4px',
                textTransform: 'none',
                padding: '7px 12px',
                fontWeight: 600,
                fontSize: '13px',
                marginRight: '8px',
              }}
              onClick={() => prev(values, TrialStepsEnum.ORG_DETAIL)}
            >
              Back
            </TabletButton>
            <TabletButton
              size='medium'
              variant='text'
              type='submit'
              style={{
                backgroundColor: theme.color.secondary.main900,
                color: '#ffffff',
                boxSizing: 'border-box',
                borderRadius: '4px',
                textTransform: 'none',
                padding: '7px 12px',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              Next
            </TabletButton>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export const TermsOfUseStep = ({
  trialData,
  next,
  prev,
}: {
  trialData: CreateTrialAccountFormValues;
  next: (newData: CreateTrialAccountFormValues, step: string) => void;
  prev: (newData: CreateTrialAccountFormValues, step: string) => void;
}) => {
  const [openSection, setOpenSection] = useState<'terms' | 'privacy' | null>(null);
  const {
    TERMS_OF_USE: { step, title, subtitle },
  } = TRIAL_STEPS;

  const { isMobile } = useCampfireTheme();

  const handleToggleSection = (section: 'terms' | 'privacy') => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <Box mx={isMobile ? '24px' : '34px'} my='28px'>
      <StepHeader step={step} title={title} subtitle={subtitle} />
      <Box my={isMobile ? 2 : 3}>
        <ExpandableSection
          title='Terms & Conditions'
          isExpanded={openSection === 'terms'}
          onToggle={() => handleToggleSection('terms')}
        >
          <TermsAndConditionsContent />
        </ExpandableSection>
        <ExpandableSection
          title='Privacy Policy'
          isExpanded={openSection === 'privacy'}
          onToggle={() => handleToggleSection('privacy')}
        >
          <PrivacyPolicyContent />
        </ExpandableSection>
      </Box>
      <Box my={3}>
        <TermsOfUseAgreementOption trialData={trialData} next={next} prev={prev} />
      </Box>
    </Box>
  );
};
